import apiClient from "./apiClient";
import { authService } from "./authService";
import { formatApiError } from "../lib/laravelError";
import { setSessionUser } from "./authSession";
import { STORAGE_KEYS, storage, mockDelay, ensureSeedData } from "./storage";

ensureSeedData();

function syncAuthUserFromApi(apiData) {
  if (!apiData || typeof apiData !== "object") return;
  const me = authService.getCurrentUser();
  if (!me?.id || Number(apiData.id) !== Number(me.id)) return;
  const name = apiData.name ?? apiData.nick ?? me.name ?? "";
  setSessionUser({
    ...me,
    ...apiData,
    email: apiData.email != null ? String(apiData.email) : me.email,
    name: name != null ? String(name) : me.name,
    nick: String(name || me.nick || ""),
  });
}

function normalizeTagList(val) {
  if (Array.isArray(val)) return val.map(String);
  if (val && typeof val === "object" && !Array.isArray(val)) {
    return Object.values(val)
      .flat()
      .map(String);
  }
  if (val == null) return [];
  return [String(val)];
}

function mapUserPayloadToProfile(apiData) {
  if (!apiData || typeof apiData !== "object") return null;
  const block = apiData.profile && typeof apiData.profile === "object" ? apiData.profile : {};
  const nick =
    apiData.name != null
      ? String(apiData.name)
      : block.nick != null
        ? String(block.nick)
        : block.name != null
          ? String(block.name)
          : "";

  return {
    userId: apiData.id ?? null,
    nick,
    physicalAttributes: normalizeTagList(
      apiData.physical_attributes ?? block.physical_attributes ?? block.physicalAttributes
    ),
    preferences: normalizeTagList(apiData.preferences ?? block.preferences),
    interests: normalizeTagList(
      apiData.kinks ?? apiData.interests ?? block.kinks ?? block.interests
    ),
    hasPartner: Boolean(apiData.hasPartner ?? block.hasPartner),
    partnerRegistered: Boolean(apiData.partnerRegistered ?? block.partnerRegistered),
    partnerId: apiData.partnerId ?? block.partnerId ?? null,
    partnerNick: apiData.partnerNick ?? block.partnerNick ?? null,
  };
}

function mapLocationFromApi(apiData) {
  const loc = apiData?.location;
  if (!loc || loc.latitude == null || loc.longitude == null) return null;
  const lat = Number(loc.latitude);
  const lng = Number(loc.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return {
    lat,
    lng,
    updatedAt: loc.updated_at ?? loc.updatedAt ?? null,
  };
}

function cacheSavedLocation(location) {
  if (!location) return;
  const current = storage.get(STORAGE_KEYS.settings, { searchRadiusKm: 25 });
  storage.set(STORAGE_KEYS.settings, {
    ...current,
    lastLocation: {
      lat: location.lat,
      lng: location.lng,
      updatedAt: location.updatedAt ?? new Date().toISOString(),
    },
  });
}

function readCachedLocation() {
  const settings = storage.get(STORAGE_KEYS.settings, {});
  const cached = settings?.lastLocation;
  if (!cached || cached.lat == null || cached.lng == null) return null;
  const lat = Number(cached.lat);
  const lng = Number(cached.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return {
    lat,
    lng,
    updatedAt: cached.updatedAt ?? null,
  };
}
function uiPatchToApiBody(patch) {
  const body = {};
  if (Object.prototype.hasOwnProperty.call(patch, "nick")) {
    body.name = patch.nick;
  }
  if (Object.prototype.hasOwnProperty.call(patch, "physicalAttributes")) {
    body.physical_attributes = patch.physicalAttributes;
  }
  if (Object.prototype.hasOwnProperty.call(patch, "preferences")) {
    body.preferences = patch.preferences;
  }
  if (Object.prototype.hasOwnProperty.call(patch, "interests")) {
    body.kinks = patch.interests;
  }
  return body;
}

export const accountService = {
  async getProfile() {
    const me = authService.getCurrentUser();
    if (!me?.id) throw new Error("No autenticado");
    try {
      const { data } = await apiClient.get(`/user/${me.id}`);
      syncAuthUserFromApi(data);
      return mapUserPayloadToProfile(data);
    } catch (err) {
      throw new Error(formatApiError(err, "No se pudo cargar el perfil"));
    }
  },

  async updateProfile(patch) {
    const me = authService.getCurrentUser();
    if (!me?.id) throw new Error("No autenticado");
    const body = uiPatchToApiBody(patch);
    try {
      const { data } = await apiClient.post(`/user/${me.id}`, body);
      syncAuthUserFromApi(data);
      return mapUserPayloadToProfile(data);
    } catch (err) {
      throw new Error(formatApiError(err, "No se pudo actualizar el perfil"));
    }
  },

  async searchPartner(query) {
    await mockDelay(160);
    const q = String(query || "").trim().toLowerCase();
    if (!q) return [];
    const registry = storage.get(STORAGE_KEYS.registry, []);
    const me = authService.getCurrentUser();
    return registry
      .filter((u) => u.nick.toLowerCase().includes(q) && u.id !== me?.id)
      .slice(0, 8);
  },

  async getSavedLocation() {
    const cached = readCachedLocation();
    const me = authService.getCurrentUser();
    if (!me?.id) return cached;

    try {
      const { data } = await apiClient.get(`/user/${me.id}`);
      const remote = mapLocationFromApi(data);
      if (remote) {
        cacheSavedLocation(remote);
        return remote;
      }
    } catch {
      // Fallback al caché local.
    }

    return cached;
  },

  async saveLocation({ lat, lng, latitude, longitude }) {
    const latNum = latitude ?? lat;
    const lngNum = longitude ?? lng;
    if (!Number.isFinite(Number(latNum)) || !Number.isFinite(Number(lngNum))) {
      throw new Error("Coordenadas inválidas");
    }

    const payload = {
      latitude: Number(latNum),
      longitude: Number(lngNum),
    };

    try {
      const { data } = await apiClient.post("/user/location", payload);
      syncAuthUserFromApi(data);
      const saved = mapLocationFromApi(data) ?? {
        lat: payload.latitude,
        lng: payload.longitude,
        updatedAt: new Date().toISOString(),
      };
      cacheSavedLocation(saved);
      return saved;
    } catch (err) {
      throw new Error(formatApiError(err, "No se pudo guardar la ubicación"));
    }
  },

  async getSettings() {
    await mockDelay(80);
    return storage.get(STORAGE_KEYS.settings, { searchRadiusKm: 25 });
  },

  async updateSettings(patch) {
    await mockDelay(80);
    const current = await this.getSettings();
    const updated = { ...current, ...patch };
    storage.set(STORAGE_KEYS.settings, updated);
    return updated;
  },
};
